<template>
  <NavBar />
  <v-container fluid style="margin-top: 185px;">
    <v-row>
      <v-col cols="12">
        <h1 class="text-h4 mb-4">Comisioane MLM</h1>
      </v-col>
    </v-row>

    <!-- Balance Card -->
    <v-row>
      <v-col cols="12" md="4">
        <v-card>
          <v-card-title class="bg-primary text-white">
            Sold Disponibil
          </v-card-title>
          <v-card-text class="text-center py-6">
            <div class="text-h3 font-weight-bold">
              {{ formatCurrency(balance?.available_balance || 0) }}
            </div>
            <div class="text-caption text-grey mt-2">
              Total castigat: {{ formatCurrency(balance?.total_earned || 0) }}
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn
              color="success"
              variant="outlined"
              block
              :disabled="!balance?.available_balance || balance.available_balance <= 0"
              @click="openPayoutDialog"
            >
              Solicita Retragere
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card>
          <v-card-title class="bg-info text-white">
            Rank Curent
          </v-card-title>
          <v-card-text class="text-center py-6">
            <div
              class="text-h4 font-weight-bold"
              :style="{ color: rankData?.current_rank?.color || '#666' }"
            >
              {{ rankData?.current_rank?.name || 'Bronze' }}
            </div>
            <div class="text-caption text-grey mt-2">
              Bonus comision: +{{ rankData?.current_rank?.commission_bonus_percentage || 0 }}%
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn color="info" variant="outlined" block @click="showRankDetails = true">
              Vezi Progres
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card>
          <v-card-title class="bg-success text-white">
            Referrals
          </v-card-title>
          <v-card-text class="text-center py-6">
            <div class="text-h3 font-weight-bold">
              {{ referralStats?.total_referrals || 0 }}
            </div>
            <div class="text-caption text-grey mt-2">
              Linkuri active: {{ referralStats?.active_referral_links || 0 }}
            </div>
          </v-card-text>
          <v-card-actions>
            <v-btn color="success" variant="outlined" block @click="$router.push('/referrals')">
              Gestioneaza Referrals
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- Transactions Table -->
    <v-row class="mt-4">
      <v-col cols="12">
        <v-card>
          <v-card-title>
            Istoric Tranzactii Comisioane
            <v-spacer />
            <v-btn icon="mdi-refresh" variant="text" @click="loadTransactions" :loading="loadingTransactions" />
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="transactionHeaders"
              :items="transactions"
              :loading="loadingTransactions"
              :items-per-page="10"
              class="elevation-1"
            >
              <template #[`item.commission_amount`]="{ item }">
                <span class="text-success font-weight-bold">
                  +{{ formatCurrency(item.commission_amount) }}
                </span>
              </template>
              <template #[`item.status`]="{ item }">
                <v-chip
                  :color="getStatusColor(item.status)"
                  size="small"
                >
                  {{ item.status }}
                </v-chip>
              </template>
              <template #[`item.created_at`]="{ item }">
                {{ formatDate(item.created_at) }}
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Payout Dialog -->
    <v-dialog v-model="payoutDialog" max-width="500">
      <v-card>
        <v-card-title>Solicita Retragere</v-card-title>
        <v-card-text>
          <v-text-field
            v-model.number="payoutAmount"
            label="Suma"
            type="number"
            :max="balance?.available_balance"
            prefix="EUR"
            :rules="[v => v > 0 || 'Suma trebuie sa fie pozitiva', v => v <= (balance?.available_balance || 0) || 'Suma depaseste soldul disponibil']"
          />
          <v-select
            v-model="payoutMethod"
            :items="payoutMethods"
            label="Metoda de retragere"
          />
          <v-textarea
            v-if="payoutMethod === 'bank_transfer'"
            v-model="bankDetails"
            label="Detalii bancare (IBAN, titular, banca)"
            rows="3"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="payoutDialog = false">Anuleaza</v-btn>
          <v-btn
            color="success"
            @click="submitPayout"
            :loading="submittingPayout"
            :disabled="!payoutAmount || payoutAmount <= 0"
          >
            Trimite Cererea
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Rank Details Dialog -->
    <v-dialog v-model="showRankDetails" max-width="600">
      <v-card>
        <v-card-title>Progres Rank</v-card-title>
        <v-card-text>
          <div v-if="rankData?.next_rank" class="mb-4">
            <div class="text-subtitle-1 mb-2">
              Progres catre {{ rankData.next_rank.name }}:
            </div>
            <div class="mb-2">
              <div class="d-flex justify-space-between text-caption">
                <span>Volum personal</span>
                <span>{{ rankData.progress?.personal_volume || 0 }} / {{ rankData.next_rank.min_personal_volume }}</span>
              </div>
              <v-progress-linear
                :model-value="getProgress(rankData.progress?.personal_volume, rankData.next_rank.min_personal_volume)"
                color="primary"
                height="8"
                rounded
              />
            </div>
            <div class="mb-2">
              <div class="d-flex justify-space-between text-caption">
                <span>Volum grup</span>
                <span>{{ rankData.progress?.group_volume || 0 }} / {{ rankData.next_rank.min_group_volume }}</span>
              </div>
              <v-progress-linear
                :model-value="getProgress(rankData.progress?.group_volume, rankData.next_rank.min_group_volume)"
                color="success"
                height="8"
                rounded
              />
            </div>
            <div>
              <div class="d-flex justify-space-between text-caption">
                <span>Referrals directi</span>
                <span>{{ rankData.progress?.direct_referrals || 0 }} / {{ rankData.next_rank.min_direct_referrals }}</span>
              </div>
              <v-progress-linear
                :model-value="getProgress(rankData.progress?.direct_referrals, rankData.next_rank.min_direct_referrals)"
                color="info"
                height="8"
                rounded
              />
            </div>
          </div>
          <v-divider class="my-4" />
          <div class="text-subtitle-1 mb-2">Istoric Rank-uri:</div>
          <v-list density="compact">
            <v-list-item v-for="history in rankData?.rank_history" :key="history.achieved_at">
              <v-list-item-title>{{ history.rank_name }}</v-list-item-title>
              <v-list-item-subtitle>{{ formatDate(history.achieved_at) }}</v-list-item-subtitle>
            </v-list-item>
            <v-list-item v-if="!rankData?.rank_history?.length">
              <v-list-item-subtitle>Nicio schimbare de rank</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" color="red" @click="showRankDetails = false">Inchide</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { getCommissionBalance, getCommissionTransactions, requestCommissionPayout, getMyRank, getMyReferrals } from '@/composables/useMlmAPI'
import NavBar from '@/components/NavBar.vue'

export default {
  name: 'CommissionsDashboard',
  components: { NavBar },
  data() {
    return {
      // Data
      balance: null,
      transactions: [],
      rankData: null,
      referralStats: null,

      // Loading states
      loadingBalance: false,
      loadingTransactions: false,
      loadingRank: false,

      // Dialogs
      payoutDialog: false,
      showRankDetails: false,

      // Payout form
      payoutAmount: 0,
      payoutMethod: 'bank_transfer',
      bankDetails: '',
      submittingPayout: false,
      payoutMethods: [
        { title: 'Transfer bancar', value: 'bank_transfer' },
        { title: 'Credit in cont', value: 'balance_credit' }
      ],

      // Snackbar
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',

      transactionHeaders: [
        { title: 'Data', key: 'created_at', sortable: true },
        { title: 'Cumparator', key: 'purchaser_client_name' },
        { title: 'Nivel', key: 'level' },
        { title: 'Suma achizitie', key: 'purchase_amount' },
        { title: 'Comision %', key: 'commission_percentage' },
        { title: 'Comision', key: 'commission_amount' },
        { title: 'Status', key: 'status' }
      ]
    }
  },
  computed: {
    token() {
      return this.$store.getters.getToken
    }
  },

mounted() {
    this.loadBalance()
    this.loadTransactions()
    this.loadRankData()
    this.loadReferralStats()
  },
  methods: {
    async loadBalance() {
      this.loadingBalance = true
      const result = await getCommissionBalance(this.token)
      if (!result.error) {
        this.balance = result.balance
      }
      this.loadingBalance = false
    },

    async loadTransactions() {
      this.loadingTransactions = true
      const result = await getCommissionTransactions(this.token, { per_page: 50 })
      if (!result.error) {
        this.transactions = result.transactions
      }
      this.loadingTransactions = false
    },

    async loadRankData() {
      this.loadingRank = true
      const result = await getMyRank(this.token)
      if (!result.error) {
        this.rankData = result
      }
      this.loadingRank = false
    },

    async loadReferralStats() {
      const result = await getMyReferrals(this.token)
      if (!result.error) {
        this.referralStats = result.stats
      }
    },

    openPayoutDialog() {
      this.payoutAmount = this.balance?.available_balance || 0
      this.payoutDialog = true
    },

    async submitPayout() {
      this.submittingPayout = true
      const result = await requestCommissionPayout(
        this.token,
        this.payoutAmount,
        this.payoutMethod,
        this.payoutMethod === 'bank_transfer' ? this.bankDetails : null
      )
      this.submittingPayout = false

      if (result.error) {
        this.snackbarMessage = result.message
        this.snackbarColor = 'error'
      } else {
        this.snackbarMessage = 'Cererea de retragere a fost trimisa!'
        this.snackbarColor = 'success'
        this.payoutDialog = false
        this.loadBalance()
      }
      this.snackbar = true
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'EUR' }).format(value || 0)
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getStatusColor(status) {
      const colors = {
        pending: 'warning',
        approved: 'success',
        paid: 'info',
        cancelled: 'error'
      }
      return colors[status] || 'grey'
    },

    getProgress(current, target) {
      if (!target || target === 0) return 100
      return Math.min((current || 0) / target * 100, 100)
    }
  }
}
</script>
